import { useCallback, useEffect, useRef, useState } from "react";

/** Delay before a hover opens the menu, so sweeping the mouse across the header does not pop it. */
export const HOVER_OPEN_DELAY_MS = 250;

/**
 * Header-menu behaviour shared by the dashboard column filters: hovering the
 * trigger (after a short delay) or clicking it opens the menu anchored to the
 * trigger; leaving before the delay cancels the open; an open menu stays open
 * until `close()` (click-away / Escape handled by the menu component itself).
 */
export function useHoverOpenMenu<T extends HTMLElement = HTMLDivElement>() {
  const [anchorEl, setAnchorEl] = useState<T | null>(null);
  const triggerRef = useRef<T | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const open = useCallback(() => {
    clearTimer();
    if (triggerRef.current) setAnchorEl(triggerRef.current);
  }, [clearTimer]);

  const close = useCallback(() => {
    clearTimer();
    setAnchorEl(null);
  }, [clearTimer]);

  const onMouseEnter = useCallback(() => {
    if (anchorEl) return;
    clearTimer();
    timerRef.current = setTimeout(open, HOVER_OPEN_DELAY_MS);
  }, [anchorEl, clearTimer, open]);

  const onMouseLeave = useCallback(() => clearTimer(), [clearTimer]);

  return {
    anchorEl,
    isOpen: Boolean(anchorEl),
    open,
    close,
    triggerProps: {
      ref: triggerRef,
      onMouseEnter,
      onMouseLeave,
      onClick: open,
      role: "button" as const,
      "aria-haspopup": "menu" as const,
      "aria-expanded": Boolean(anchorEl),
    },
  };
}
